import { EnvironmentInjector, createEnvironmentInjector } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, type Observable, type Subscription } from 'rxjs';

export interface SentryReporterOptions {
  enabled: boolean;
  error?: unknown;
  context?: string;
  data?: unknown;
  message: string;
  loadSentry?: () => Promise<typeof import('@sentry/angular')>;
}

export interface DeferredObservabilityOptions {
  enabled: boolean;
  injector: EnvironmentInjector;
  router: Pick<Router, 'events' | 'url'>;
  document: Pick<Document, 'title'>;
  createChildInjector?: typeof createEnvironmentInjector;
  loadMatomo?: () => Promise<typeof import('ngx-matomo-client/core')>;
  onError?: (error: unknown) => void;
  schedule?: (task: () => void) => void;
}

const MATOMO_CONFIGURATION = {
  trackerUrl: 'https://matomo.monitor.qlick.cloud',
  siteId: '1',
  trackAppInitialLoad: false,
} as const;

export async function reportErrorToSentry({
  enabled,
  error,
  context,
  data,
  message,
  loadSentry = () => import('@sentry/angular'),
}: SentryReporterOptions): Promise<void> {
  if (!enabled || !error) {
    return;
  }

  const Sentry = await loadSentry();
  Sentry.captureException(error, {
    extra: {
      message,
      context,
      data,
    },
  });
}

export async function initializeDeferredObservability({
  enabled,
  injector,
  router,
  document,
  createChildInjector = createEnvironmentInjector,
  loadMatomo = () => import('ngx-matomo-client/core'),
}: DeferredObservabilityOptions): Promise<(() => void) | void> {
  if (!enabled) {
    return;
  }

  const matomo = await loadMatomo();
  const childInjector = createChildInjector([matomo.provideMatomo(MATOMO_CONFIGURATION)], injector);
  const initializer = childInjector.get(matomo.MatomoInitializerService);
  const tracker = childInjector.get(matomo.MatomoTracker);

  initializer.initialize();

  const trackPageView = (url: string): void => {
    tracker.setCustomUrl(url);
    tracker.setDocumentTitle(document.title);
    tracker.trackPageView();
  };

  trackPageView(router.url);

  const subscription = trackNavigationEnd(router.events, trackPageView);

  return () => {
    subscription.unsubscribe();
    childInjector.destroy();
  };
}

export function scheduleDeferredObservability({
  enabled,
  onError = error => console.error('Deferred observability bootstrap failed', error),
  schedule = defaultSchedule,
  ...options
}: DeferredObservabilityOptions): void {
  if (!enabled) {
    return;
  }

  schedule(() => {
    void initializeDeferredObservability({ enabled, onError, schedule, ...options }).catch(onError);
  });
}

function defaultSchedule(task: () => void): void {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    window.requestIdleCallback(() => task());
    return;
  }

  globalThis.setTimeout(task, 0);
}

function trackNavigationEnd(
  events: Observable<unknown>,
  trackPageView: (url: string) => void
): Subscription {
  return events
    .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
    .subscribe(event => {
      trackPageView(event.urlAfterRedirects);
    });
}
